import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { withWorkspaceFilter, getActiveWorkspaceId } from "@/utils/workspace-helpers";

export interface PowerOfficeIntegration {
  id: string;
  company_name: string | null;
  organization_number: string | null;
  is_active: boolean;
  created_at: string;
  token_expires_at: string | null;
}

export function usePowerOfficeIntegration() {
  const useHasActivePowerOfficeQuery = () => {
    return useQuery({
      queryKey: ["has-active-poweroffice", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        const { data, error } = await withWorkspaceFilter(
          supabase.from("poweroffice_integrations")
          .select("id")
          .eq("is_active", true)
          .limit(1)
        );

        if (error) {
          throw error;
        }

        return data && data.length > 0;
      },
    });
  };

  const useLastSyncTimeQuery = () => {
    return useQuery({
      queryKey: ["poweroffice-last-sync", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data, error } = await withWorkspaceFilter(
          supabase.from("poweroffice_detection_runs")
          .select("run_at")
          .order("run_at", { ascending: false })
          .limit(1)
          .single()
        );

        if (error) {
          if (error.code === "PGRST116") {
            return null;
          }
          throw error;
        }

        return data?.run_at ? new Date(data.run_at) : null;
      },
      refetchInterval: 60000, // Refetch every minute
    });
  };

  const usePowerOfficeIntegrationsQuery = () => {
    return useQuery({
      queryKey: ["poweroffice-integrations", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await withWorkspaceFilter(
          supabase.from("poweroffice_integrations")
          .select("*")
          .order("created_at", { ascending: false })
        );

        if (error) throw error;
        return (data || []) as PowerOfficeIntegration[];
      },
    });
  };

  const useActivePowerOfficeIntegrationsQuery = () => {
    return useQuery({
      queryKey: ["poweroffice-integrations-active", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await withWorkspaceFilter(
          supabase.from("poweroffice_integrations")
          .select("*")
          .eq("is_active", true)
          .order("created_at", { ascending: false })
        );

        if (error) throw error;
        return (data || []) as PowerOfficeIntegration[];
      },
    });
  };

  return {
    useHasActivePowerOfficeQuery,
    useLastSyncTimeQuery,
    usePowerOfficeIntegrationsQuery,
    useActivePowerOfficeIntegrationsQuery,
  };
}
